import React, { useState, useEffect, useCallback, useRef } from 'react';

// Constantes
const IMAGE_DURATION = 250; // Durée d'affichage de chaque image (0.5 secondes)
const SEQUENCE_LENGTH = 10; // Nombre d'images consécutives avant changement aléatoire
const TOTAL_IMAGES = 339; // Nombre total d'images
const INITIAL_BATCH_SIZE = 50; // Nombre d'images à charger initialement

// Génération du tableau des chemins d'images
const IMAGES = Array.from({ length: TOTAL_IMAGES }, (_, i) => {
    const number = String(i + 1).padStart(5, '0');
    return `/assets/images/arome_img_${number}.png`;
});

const BackgroundImages = () => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [currentSequence, setCurrentSequence] = useState<number[]>([]);
    const [loadedImages, setLoadedImages] = useState<number[]>([]);
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

    // Utilisation de useRef pour stocker les images préchargées
    const imageCache = useRef<{ [key: string]: HTMLImageElement }>({});
    const lastUpdateTime = useRef<number>(0);
    const animationFrameId = useRef<number>();

    // Fonction de chargement d'une image
    const loadImage = useCallback((src: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = src;
            img.onload = () => {
                imageCache.current[src] = img;
                resolve();
            };
            img.onerror = () => reject();
        });
    }, []);

    // Charge un lot d'images
    const loadImageBatch = useCallback(async (startIndex: number, batchSize: number) => {
        const endIndex = Math.min(startIndex + batchSize, TOTAL_IMAGES);
        const loadPromises = [];

        for (let i = startIndex; i < endIndex; i++) {
            loadPromises.push(
                loadImage(IMAGES[i])
                    .then(() => {
                        setLoadedImages(prev => {
                            if (!prev.includes(i)) {
                                return [...prev, i];
                            }
                            return prev;
                        });
                    })
                    .catch(error => {
                        console.error(`Failed to load image ${i}:`, error);
                    })
            );
        }

        await Promise.all(loadPromises);
    }, [loadImage]);

    // Génère une nouvelle séquence aléatoire d'images
    const generateNewSequence = useCallback(() => {
        const availableIndices = [...loadedImages];
        const sequence: number[] = [];

        for (let i = 0; i < SEQUENCE_LENGTH; i++) {
            if (availableIndices.length === 0) break;
            const randomIndex = Math.floor(Math.random() * availableIndices.length);
            sequence.push(availableIndices[randomIndex]);
            availableIndices.splice(randomIndex, 1);
        }

        while (sequence.length < SEQUENCE_LENGTH) {
            sequence.push(sequence[Math.floor(Math.random() * sequence.length)]);
        }

        return sequence;
    }, [loadedImages]);

    // Animation frame loop pour un timing plus précis
    const updateImage = useCallback(() => {
        const currentTime = performance.now();
        const elapsed = currentTime - lastUpdateTime.current;

        if (elapsed >= IMAGE_DURATION) {
            setCurrentImageIndex(prevIndex => {
                if (prevIndex >= SEQUENCE_LENGTH - 1) {
                    setCurrentSequence(generateNewSequence());
                    return 0;
                }
                return prevIndex + 1;
            });
            lastUpdateTime.current = currentTime;
        }

        animationFrameId.current = requestAnimationFrame(updateImage);
    }, [generateNewSequence]);

    // Chargement initial des images
    useEffect(() => {
        const initialLoad = async () => {
            await loadImageBatch(0, INITIAL_BATCH_SIZE);
            setIsInitialLoadComplete(true);

            // Chargement en arrière-plan
            let currentIndex = INITIAL_BATCH_SIZE;
            const BACKGROUND_BATCH_SIZE = 20;

            while (currentIndex < TOTAL_IMAGES) {
                await loadImageBatch(currentIndex, BACKGROUND_BATCH_SIZE);
                currentIndex += BACKGROUND_BATCH_SIZE;
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        };

        initialLoad();
    }, [loadImageBatch]);

    // Gestion de l'animation
    useEffect(() => {
        if (!isInitialLoadComplete) return;

        setCurrentSequence(generateNewSequence());
        lastUpdateTime.current = performance.now();
        animationFrameId.current = requestAnimationFrame(updateImage);

        return () => {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        };
    }, [isInitialLoadComplete, generateNewSequence, updateImage]);

    // Nettoyage au démontage
    useEffect(() => {
        return () => {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        };
    }, []);

    if (!isInitialLoadComplete) {
        return (
            <div className="fixed top-0 left-0 w-full h-full bg-black flex items-center justify-center text-white">
                Loading initial images...
            </div>
        );
    }

    return (
        <div className="fixed top-0 left-0 w-full h-full z-10">
            {currentSequence.length > 0 && (
                <img
                    src={IMAGES[currentSequence[currentImageIndex]]}
                    alt={`Background ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                    style={{ imageRendering: 'auto' }}
                />
            )}
        </div>
    );
};

export default BackgroundImages;